"use client";
import { useEffect } from "react";
import gsap from "gsap";
import { usePathname } from "next/navigation";

export default function Home() {
  const pathname = usePathname();

  const runAnimations = () => {
    const paths = [
      "#p1-glow",
      "#p2-glow",
      "#p3-glow",
      "#p4-glow",
      "#p5-glow",
      "#p6-glow",
      "#p7-glow",
      "#p8-glow",
      "#p9-glow"
    ];

    const duration = 5; // seconds
    const segmentLength = 50; // visible glowing part length

    paths.forEach((selector) => {
      const path = document.querySelector(selector) as SVGPathElement | null;
      if (!path) return 0;
      const length = path.getTotalLength();

      gsap.fromTo(
        selector,
        {
          strokeDasharray: `${segmentLength} ${length}`,
          strokeDashoffset: 0
        },
        {
          strokeDashoffset: length + segmentLength, // extra segment for smooth wrap
          duration,
          repeat: -1,
          ease: "none"
        }
      );
    });

    // Smooth entry animation for page elements
    gsap.from(".animate-header h1", {
      opacity: 0,
      y: -50,
      duration: 1.4,
      ease: "power3.out",
    });

    gsap.from(".animate-header p", {
      opacity: 0,
      y: -50,
      duration: 1.4,
      ease: "power3.out",
      delay: 1,
    });

    gsap.from("svg", {
      opacity: 0,
      scale: 0.95,
      duration: 2,
      ease: "power3.out",
      delay: 0.3,
    });

    gsap.from("image", {
      opacity: 0,
      scale: 0.95,
      y: -50,
      duration: 2,
      ease: "power3.out",
      delay: 0.3,
    });
  };

  useEffect(() => {
    // Run animations when component mounts or when pathname changes to "/" (homepage)
    if (pathname === "/") {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        runAnimations();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [pathname]);


  return (
    <div className="wire-animation">
      <div className="animate-header">
        <h1 className="blu-head">Ideas in Orbit, Solutions on Earth</h1>
        <p>Think Big, We Will Handle the Cloud</p>
      </div>

    <div
      style={{
        // background: "#111",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // height: "100vh",
        margin: "50px 0 0",
      }}
    >
      <svg
        viewBox="0 0 1382 390"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: "100%", height: "auto" }}
      >
        <defs>
          {/* Glow filter */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"></feGaussianBlur>
            <feMerge>
              <feMergeNode in="coloredBlur"></feMergeNode>
              <feMergeNode in="SourceGraphic"></feMergeNode>
            </feMerge>
          </filter>
        </defs>

        {/* Paths */}
        <path id="p1" d="M0 311C162.5 310.333 487.5 311.231 490.5 311C510 309.5 546 321 556.5 257.5C569 196 575.5 122.5 628 123C670 123.4 686.833 123.167 690 123" stroke="#222" strokeWidth="2" fill="none" />
        <path id="p1-glow" d="M0 311C162.5 310.333 487.5 311.231 490.5 311C510 309.5 546 321 556.5 257.5C569 196 575.5 122.5 628 123C670 123.4 686.833 123.167 690 123" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

        <path id="p2" d="M359 255.001L434 254.09C443 252.589 450.9 249.201 468.5 202.001C485 164.501 498.5 120.001 535.5 113.001C548 110.635 639.5 113.001 689 113.001" stroke="#222" strokeWidth="2" fill="none" />
        <path id="p2-glow" d="M359 255.001L434 254.09C443 252.589 450.9 249.201 468.5 202.001C485 164.501 498.5 120.001 535.5 113.001C548 110.635 639.5 113.001 689 113.001" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

        <path id="p3" d="M197.5 155.5H418C426 155 439.5 144.1 459.5 126.5C471.5 116.5 484 103 510 103C530.8 103 638.667 103 690 103" stroke="#222" strokeWidth="2" fill="none" />
        <path id="p3-glow" d="M197.5 155.5H418C426 155 439.5 144.1 459.5 126.5C471.5 116.5 484 103 510 103C530.8 103 638.667 103 690 103" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

        <path id="p4" d="M284 21.5 H348.5 C357.333 21.6667 373 15 373 43 C373 39.8 372.245 56.7118 373 63 C374.5 75.5 386.2 92.5 411 92.5 C435.8 92.9 606.667 93.3333 689 93.5" stroke="#222" strokeWidth="2" fill="none" />
        <path id="p4-glow" d="M284 21.5 H348.5 C357.333 21.6667 373 15 373 43 C373 39.8 372.245 56.7118 373 63 C374.5 75.5 386.2 92.5 411 92.5 C435.8 92.9 606.667 93.3333 689 93.5" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

        {/* Group with p5-p8 */}
        <g transform="translate(691, 0)">
          <path id="p5" d="M691 311C528.5 310.333 203.5 311.231 200.5 311C181 309.5 145 321 134.5 257.5C122 196 115.5 122.5 63 123C21 123.4 4.16667 123.167 1 123" stroke="#222" strokeWidth="2" fill="none" />
          <path id="p5-glow" d="M691 311C528.5 310.333 203.5 311.231 200.5 311C181 309.5 145 321 134.5 257.5C122 196 115.5 122.5 63 123C21 123.4 4.16667 123.167 1 123" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

          <path id="p6" d="M332 255.001L257 254.09C248 252.589 240.1 249.201 222.5 202.001C206 164.501 192.5 120.001 155.5 113.001C143 110.635 51.5 113.001 2 113.001" stroke="#222" strokeWidth="2" fill="none" />
          <path id="p6-glow" d="M332 255.001L257 254.09C248 252.589 240.1 249.201 222.5 202.001C206 164.501 192.5 120.001 155.5 113.001C143 110.635 51.5 113.001 2 113.001" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

          <path id="p7" d="M493.5 155.5H273C265 155 251.5 144.1 231.5 126.5C219.5 116.5 207 103 181 103C160.2 103 52.3333 103 1 103" stroke="#222" strokeWidth="2" fill="none" />
          <path id="p7-glow" d="M493.5 155.5H273C265 155 251.5 144.1 231.5 126.5C219.5 116.5 207 103 181 103C160.2 103 52.3333 103 1 103" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

          <path id="p8" d="M405 21.5 H340.5 C331.667 21.6667 316 15 316 43 C316 39.8 316.755 56.7118 316 63 C314.5 75.5 302.8 92.5 278 92.5 C253.2 92.9 82.333 93.3333 0 93.5" stroke="#222" strokeWidth="2" fill="none" />
          <path id="p8-glow" d="M405 21.5 H340.5 C331.667 21.6667 316 15 316 43 C316 39.8 316.755 56.7118 316 63 C314.5 75.5 302.8 92.5 278 92.5 C253.2 92.9 82.333 93.3333 0 93.5" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />

{/* Right images */}

  <rect x="405" y="0" width="60" height="50" rx="10" ry="10" fill="#252525" />
  <image href="/assets/images/aws.svg" x="410" y="0" width="50" height="50" />

  <rect x="490" y="130" width="60" height="50" rx="10" ry="10" fill="#252525" />
  <image href="/assets/images/Ai-icn.svg" x="500" y="130" width="40" height="50" />

  <rect x="330" y="230" width="60" height="50" rx="10" ry="10" fill="#252525" />
  <image href="/assets/images/gCloud.svg" x="330" y="230" width="60" height="50" />

{/*  */}


        </g>


{/* Vertical lines from center node */}
<path id="p9" d="M681 170 V390" stroke="#222" strokeWidth="2" fill="none" />
<path id="p9-glow" d="M681 170 V390" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" />
{/* <path id="p9" d="M681 170 V390" stroke="#222" strokeWidth="2" fill="none" /> */}
{/* <path id="p9-line" d="M681 170 V390" stroke="#222" strokeWidth="2" fill="none" /> */}
<path id="p10-line" d="M688.8 170 V390" stroke="#222" strokeWidth="2" fill="none" />
<path id="p11-line" d="M697 170 V390" stroke="#222" strokeWidth="2" fill="none" />
<path id="p12-line" d="M705 170 V390" stroke="#222" strokeWidth="2" fill="none" />

{/* <path id="p9-glow" d="M681 170 V390" stroke="#00f0ff" strokeWidth="4" fill="none" filter="url(#glow)" /> */}



{/* LEFT Images */}
        <rect x="225" y="0" width="60" height="50" rx="10" ry="10" fill="#252525" />
        <image href="/assets/images/Stripe.svg" x="230" y="2" width="50" height="50" />

        <rect x="137" y="130" width="60" height="50" rx="10" ry="10" fill="#252525" />
        <image href="/assets/images/Github.svg" x="142" y="130" width="50" height="50" />

        <rect x="305" y="230" width="60" height="50" rx="10" ry="10" fill="#252525" />
        <image href="/assets/images/azure.svg" x="310" y="230" width="50" height="50" />

        <image href="/assets/images/center-updatedd.svg" x="628" y="45" width="130" height="130" />
{/*  */}
      </svg>
    </div>

    </div>
  );
}
